import { useCallback, useEffect, useState } from 'react';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { ReportFeatures } from '@atlassian/jira-navigation-apps-sidebar-servicedesk/src/common/types/index.tsx';
import { createReportUrl } from '@atlassian/jira-navigation-apps-sidebar-servicedesk/src/common/utils/index.tsx';
import { useCustomReports } from '@atlassian/jira-navigation-apps-sidebar-servicedesk/src/controllers/custom-reports/index.tsx';
import { useReportFeatures } from '@atlassian/jira-navigation-apps-sidebar-servicedesk/src/controllers/features/main.tsx';
import { servicedeskReports1Route } from '@atlassian/jira-router-routes-servicedesk-reports-routes/src/index.tsx';
import type { Item } from '@atlassian/jira-servicedesk-reports-nav-api/src/types.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import {
	WorkloadThumbnail,
	SatisfactionThumbnail,
	RequestsDeflectedThumbnail,
	RequestsResolvedThumbnail,
	CustomReportsThumbnail,
	type ReportsConfig,
	type Report,
} from '@atlassian/jsm-enterprise-reports-common';
import { REPORT_TYPE } from '../../common/constants.tsx';
import messages from './messages.tsx';

export const useReportsHomePageController = (projectKey: ProjectKey) => {
	const { formatMessage } = useIntl();
	const [loading, setLoading] = useState(true);
	const [reportsConfig, setReportsConfig] = useState<ReportsConfig[]>([]);
	const { data: defaultReports, loading: defaultReportsLoading } = useReportFeatures({
		route: servicedeskReports1Route,
		matchParams: { projectKey },
	});
	const licensedProducts = useLicensedProducts();
	const shouldShowAtlassianAnalytics = licensedProducts[LICENSED_PRODUCTS.ATLASSIAN_ANALYTICS];
	const { data: initialCustomReportsFromSidebarConfig, loading: customReportsLoading } =
		useCustomReports();

	const getDefaultReports = useCallback(
		(features: ReportFeatures): ReportsConfig => {
			const reports: Report[] = [];
			let defaultReportsAccess = false;

			if (features.hasWorkload) {
				defaultReportsAccess = true;
				reports.push({
					link: createReportUrl(projectKey, '/workload'),
					thumbnail: WorkloadThumbnail,
					title: formatMessage(messages.workload),
				});
			}
			if (features.hasSatisfaction) {
				defaultReportsAccess = true;
				reports.push({
					link: createReportUrl(projectKey, '/all-rating'),
					thumbnail: SatisfactionThumbnail,
					title: formatMessage(messages.satisfaction),
				});
			}
			if (features.hasRequestsDeflected) {
				defaultReportsAccess = true;
				reports.push({
					link: createReportUrl(projectKey, '/kb-requests-deflected'),
					thumbnail: RequestsDeflectedThumbnail,
					title: formatMessage(messages.requestsDeflected),
				});
			}
			if (features.hasRequestsDeflected) {
				defaultReportsAccess = true;
				reports.push({
					link: createReportUrl(projectKey, '/kb-requests-resolved'),
					thumbnail: RequestsResolvedThumbnail,
					title: formatMessage(messages.requestsResolved),
				});
			}
			if (shouldShowAtlassianAnalytics) {
				defaultReportsAccess = true;
				reports.push({
					link: createReportUrl(projectKey, '/atlassian-analytics'),
					thumbnail: CustomReportsThumbnail,
					title: formatMessage(messages.analytics),
				});
			}

			return {
				id: REPORT_TYPE.DEFAULT,
				sectionName: formatMessage(messages.defaultReportsTitle),
				reports,
				access: defaultReportsAccess,
			};
		},
		[formatMessage, projectKey, shouldShowAtlassianAnalytics],
	);

	const getCustomReportsConfig = useCallback(
		(config: Item[]): ReportsConfig => {
			const reports = config.map((report) => ({
				link: __SPA__
					? report.url.replace(/^\/projects/, '/jira/servicedesk/projects')
					: report.url,
				thumbnail: CustomReportsThumbnail,
				title: report.name,
			}));
			return {
				id: REPORT_TYPE.CUSTOM,
				sectionName: formatMessage(messages.customReportsTitle),
				reports,
				access: reports.length > 0,
			};
		},
		[formatMessage],
	);

	useEffect(() => {
		if (customReportsLoading || defaultReportsLoading) {
			return;
		}
		const config = [];

		if (defaultReports) {
			config.push(getDefaultReports(defaultReports));
		}
		if (initialCustomReportsFromSidebarConfig) {
			config.push(getCustomReportsConfig(initialCustomReportsFromSidebarConfig));
		}
		setReportsConfig(config);
	}, [
		customReportsLoading,
		defaultReportsLoading,
		defaultReports,
		initialCustomReportsFromSidebarConfig,
		getDefaultReports,
		setReportsConfig,
		getCustomReportsConfig,
	]);
	useEffect(() => {
		setLoading(customReportsLoading || defaultReportsLoading);
	}, [customReportsLoading, defaultReportsLoading, setLoading]);

	return {
		reportsConfig,
		loading,
	};
};
